// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Edit3, Trash2 } from "lucide-react";
import { toast } from 'sonner';
import helpers from '@/utils/helpers';
import getLocationDisplayName from '@staty/shared/utils/getLocationDisplayName';
import getDefaultGameDuration from '@staty/shared/utils/getDefaultGameDuration';

interface Team {
  _id: string;
  name: string;
  division: string;
}

interface Game {
  _id?: string;
  timeSlot?: string;
  homeTeam?: string | Team;
  awayTeam?: string | Team;
  division: string;
  date?: string;
  globalDate?: string; // MM-DD-YYYY format
  time: string;
  location?: string;
  locationModel?: string;
  slotSectionId?: string; // Track which slot section this game belongs to
  duration?: number; // Duration in minutes
}

interface TimeSlot {
  _id: string;
  location: {
    _id: string;
    name: string;
  };
  availableDate: string;
  startTime: string;
  endTime: string;
  maxGames: number;
  timeZone: string;
}

interface SlotSection {
  id: string;
  timeSlotId: string;
  game?: Game;
  divisionId: string;
  slotTime: string; // Calculated time for this specific slot
  duration?: number; // Duration in minutes for this slot section
}

interface Division {
  _id: string;
  name: string;
  sports: string[];
  config?: {
    gameDurationInMinutes?: number;
  };
}

export const DraggableTeam = React.memo(({ team, isDragOverlay = false }: { team: Team; isDragOverlay?: boolean }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: team._id });

  const style = React.useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }), [transform, transition, isDragging]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`p-2 bg-card border rounded-lg cursor-grab hover:shadow-md transition-all max-w-[200px] ${
        isDragOverlay ? 'shadow-lg scale-105' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{team.name}</span>
      </div>
    </div>
  );
});

export const TeamSlot = React.memo(({ 
  teamId, 
  teamType, 
  teams, 
  slotId, 
  slotSection,
  activeTeam,
  onTeamUpdate, 
  onTeamRemove,
  divisions,
  isDragOverlay = false 
}: { 
  teamId?: string | Team;
  teamType: 'homeTeam' | 'awayTeam';
  teams: Team[];
  slotId: string;
  slotSection: SlotSection;
  activeTeam: Team | null;
  onTeamUpdate: (teamId: string, teamType: 'homeTeam' | 'awayTeam') => void;
  onTeamRemove: (teamType: 'homeTeam' | 'awayTeam') => void;
  divisions: Division[];
  isDragOverlay?: boolean;
}) => {
  const droppableId = `${slotId}-${teamType}`;
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
  });

  // Handle both string IDs and populated team objects
  const team = teamId ? 
    (typeof teamId === 'string' ? 
      teams.find(t => t._id === teamId) : 
      teamId) : 
    null;
  const isEmpty = !teamId;
  
  
  // Check if this slot can accept drops based on division compatibility
  let canAcceptDrop = isEmpty;
  
  // If there's an active team being dragged and this slot is empty, check division compatibility
  if (isEmpty && activeTeam && slotSection.game) {
    const otherTeamId = teamType === 'homeTeam' ? slotSection.game.awayTeam : slotSection.game.homeTeam;
    if (otherTeamId) {
      const otherTeam = typeof otherTeamId === 'string' ? 
        teams.find(t => t._id === otherTeamId) : 
        otherTeamId;
      if (otherTeam) {
        const otherTeamDivision = helpers.getObjId(otherTeam.division);
        const activeTeamDivision = helpers.getObjId(activeTeam.division);
        canAcceptDrop = otherTeamDivision === activeTeamDivision;
      }
    }
  }



  return (
    <div
      ref={setNodeRef}
      className={`p-2 border-2 border-dashed rounded-lg min-h-[60px] transition-all duration-200 ${
        isOver && canAcceptDrop ? 'border-primary bg-primary/10 shadow-md ring-2 ring-ring/30' :
        isOver && !canAcceptDrop ? 'border-destructive bg-destructive/10 shadow-md ring-2 ring-destructive/30' :
        team ? 'border-success/30 bg-success/10' : 'border-input bg-muted/50 hover:border-muted-foreground/50 hover:bg-muted'
      } ${isDragOverlay ? 'shadow-lg' : ''}`}
    >
      {team && (
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onTeamRemove(teamType);
            }}
            className="h-3 w-3 p-0 text-xs"
          >
            ×
          </Button>
        </div>
      )}
      
      {team ? (
        <div className="p-2 bg-card rounded border mt-2">
          <div className="text-xs font-medium text-center">{team.name}</div>
          <div className="text-xs text-muted-foreground text-center mt-1">{divisions.find(d => d._id === team.division)?.name}</div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full min-h-[40px] text-muted-foreground/60">
          <div className="text-xs text-center">{teamType === 'homeTeam' ? 'Drop home team' : 'Drop away team'}</div>
        </div>
      )}
    </div>
  );
});

export const SlotSection = React.memo(({ 
  slotSection, 
  teams, 
  timeSlot,
  selectedDivisionIds,
  divisions,
  orgSport,
  teamsByDivision,
  activeId,
  tournamentId,
  leagueId,
  onGameUpdate,
  onGameCreate,
  onGameDelete,
  formatTimeForDisplay,
  locationName,
  isDragOverlay = false,
  isDragging = false
}: {
  slotSection: SlotSection; 
  teams: Team[];
  timeSlot: TimeSlot;
  selectedDivisionIds: string[];
  divisions: Division[];
  orgSport?: string;
  teamsByDivision: Record<string, Team[]>;
  activeId: string | null;
  tournamentId: string | undefined;
  leagueId: string | undefined;
  onGameUpdate: (game: Game) => void;
  onGameCreate: (game: Omit<Game, '_id'>) => void;
  onGameDelete: (gameId: string) => void;
  formatTimeForDisplay: (timeString: string) => string;
  locationName: string;
  isDragOverlay?: boolean;
  isDragging?: boolean;
}) => {
  const handleTeamUpdate = (teamId: string, teamType: 'homeTeam' | 'awayTeam') => {
    // Find the team to determine its division
    const team = teams.find(t => t._id === teamId);
    if (!team) return;
    
    const teamDivisionId = helpers.getObjId(team.division);
    
    if (slotSection.game) {
      // Check if teams are from the same division
      const existingTeamId = slotSection.game.homeTeam || slotSection.game.awayTeam;
      const existingTeam = existingTeamId ? 
        (typeof existingTeamId === 'string' ? 
          teams.find(t => t._id === existingTeamId) : 
          existingTeamId) : 
        null;
      
      if (existingTeam && helpers.getObjId(existingTeam.division) !== teamDivisionId) {
        toast.error('Teams must be from the same division to play against each other');
        return;
      }
      
      // Update existing game
      const updatedGame = {
        ...slotSection.game,
        [teamType]: teamId,
        division: teamDivisionId, // Ensure division is set correctly
      };
      onGameUpdate(updatedGame);
    } else {
      // Create new game
      const newGame: Omit<Game, '_id'> = {
        timeSlot: slotSection.timeSlotId,
        homeTeam: teamType === 'homeTeam' ? teamId : null,
        awayTeam: teamType === 'awayTeam' ? teamId : null,
        division: teamDivisionId,
        date: timeSlot.availableDate,
        globalDate: timeSlot.availableDate,
        time: formatTimeForDisplay(slotSection.slotTime),
        location: getLocationDisplayName(timeSlot.location),
        locationModel: timeSlot.location._id,
        slotSectionId: slotSection.id,
      };
      onGameCreate(newGame);
    }
  };

  const handleTeamRemove = (teamType: 'homeTeam' | 'awayTeam') => {
    if (slotSection.game) {
      const updatedGame = {
        ...slotSection.game,
        [teamType]: null, // Use null instead of undefined
      };
      
      if (!updatedGame.homeTeam && !updatedGame.awayTeam) {
        onGameDelete(slotSection.game._id!);
      } else {
        onGameUpdate(updatedGame);
      }
    }
  };

  const handleDeleteGame = () => {
    if (slotSection.game) {
      onGameDelete(slotSection.game._id!);
    }
  };

  const isFull = slotSection.game && slotSection.game.homeTeam && slotSection.game.awayTeam;

  // Get the division of the current game (if any)
  const gameDivision = slotSection.game ? 
    divisions.find(d => d._id === slotSection.game!.division) : null;

  // Helper function to convert time to HH:MM format for input
  const convertTimeForInput = (time: string) => {
    if (!time) return time;
    if (time.includes('AM') || time.includes('PM')) {
      // Convert from 12-hour format to 24-hour format
      const timeStr = time.replace(/\s*(AM|PM)/i, '');
      const [hours, minutes] = timeStr.split(':');
      const isPM = time.toUpperCase().includes('PM');
      const hour24 = isPM ? 
        (hours === '12' ? 12 : parseInt(hours) + 12) : 
        (hours === '12' ? 0 : parseInt(hours));
      return `${hour24.toString().padStart(2, '0')}:${minutes}`;
    }
    return time;
  };

  // State for editing game time
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editedTime, setEditedTime] = useState(convertTimeForInput(slotSection.game?.time || slotSection.slotTime));

  // Sync editedTime with game time changes
  useEffect(() => {
    setEditedTime(convertTimeForInput(slotSection.game?.time || slotSection.slotTime));
  }, [slotSection.game?.time, slotSection.slotTime]);

  // Handle time update
  const handleTimeUpdate = (newTime: string) => {
    // Ensure consistent time format (HH:MM)
    const formattedTime = newTime.length === 5 ? newTime : newTime.padStart(5, '0');
    
    if (slotSection.game) {
      // Update existing game
      const updatedGame = {
        ...slotSection.game,
        time: formattedTime,
      };
      onGameUpdate(updatedGame);
    } else {
      // For empty slots, we need to create a placeholder game with just the time
      // This will allow the time to be displayed and saved
      const placeholderGame = {
        time: formattedTime,
        slotSectionId: slotSection.id,
        homeTeam: null,
        awayTeam: null,
        division: null, // Will be set when teams are added
        status: 'scheduled',
        // Required Game fields
        timeSlot: timeSlot._id,
        date: timeSlot.availableDate,
        globalDate: timeSlot.availableDate,
        location: timeSlot.location?.name || '',
        locationModel: timeSlot.location?._id,
        league: tournamentId || leagueId, // Use tournament or season ID
        season: leagueId,
        duration: getDefaultGameDuration(orgSport ?? divisions?.[0]?.sports?.[0]), // sport-aware default (pickleball 30, else 60)
        isBye: false,
        playoffRound: null,
        isCancelled: false,
        isUpdated: false,
        isHidden: false,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      };
      
      onGameCreate(placeholderGame);
    }
    setIsEditingTime(false);
  };

  return (
    <div className={`p-2 border rounded-lg min-h-[100px] transition-all max-w-full overflow-hidden ${
      isFull ? 'border-success/30 bg-success/10' : 'border-input bg-muted/50'
    } ${isDragOverlay ? 'shadow-lg' : ''} ${
      isDragging ? 'border-primary/30 bg-primary/5 shadow-sm' : ''
    }`}>
      <div className="mb-2">
        <div className="flex items-center justify-between min-w-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-xs text-primary font-medium truncate">{locationName}</span>
            <span className="text-xs text-muted-foreground">•</span>
            
            {/* Editable Time Field */}
            {isEditingTime ? (
              <div className="flex items-center gap-1">
                <Input
                  type="time"
                  value={editedTime}
                  onChange={(e) => setEditedTime(e.target.value)}
                  className="h-6 w-20 text-xs"
                  autoFocus
                  onBlur={() => handleTimeUpdate(editedTime)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleTimeUpdate(editedTime);
                    } else if (e.key === 'Escape') {
                      setIsEditingTime(false);
                      const currentTime = slotSection.game?.time || slotSection.slotTime;
                      setEditedTime(convertTimeForInput(currentTime));
                    }
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-primary">
                  {formatTimeForDisplay(slotSection.game?.time || editedTime || slotSection.slotTime)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const currentTime = slotSection.game?.time || slotSection.slotTime;
                    setEditedTime(convertTimeForInput(currentTime));
                    setIsEditingTime(true);
                  }}
                  className="h-5 w-5 p-0 hover:bg-primary/10"
                  title="Edit game time"
                >
                  <Edit3 className="h-3 w-3 text-muted-foreground hover:text-primary" />
                </Button>
              </div>
            )}
            
            {isFull && (
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-success rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              </div>
            )}
          </div>
          
          {slotSection.game && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDeleteGame}
              className="h-5 w-5 p-0"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {gameDivision && (
          <div className="text-xs text-muted-foreground mt-1">
            {gameDivision.name}
          </div>
        )}
      </div>
      
      <div className="flex gap-2">
        <div className="flex-1">
          <TeamSlot
            teamId={slotSection.game?.homeTeam}
            teamType="homeTeam"
            teams={teams}
            slotId={slotSection.id}
            slotSection={slotSection}
            activeTeam={activeId ? teams.find(t => t._id === activeId) : null}
            onTeamUpdate={handleTeamUpdate}
            onTeamRemove={handleTeamRemove}
            divisions={divisions}
          />
        </div>
        
        <div className="flex-1">
          <TeamSlot
            teamId={slotSection.game?.awayTeam}
            teamType="awayTeam"
            teams={teams}
            slotId={slotSection.id}
            slotSection={slotSection}
            activeTeam={activeId ? teams.find(t => t._id === activeId) : null}
            onTeamUpdate={handleTeamUpdate}
            onTeamRemove={handleTeamRemove}
            divisions={divisions}
          />
        </div>
      </div>
    </div>
  );
});
